import { GRAPHQL_TRACE_KEY } from "@digits-shared/components/Contexts/GraphQLTracerContext"
import { DIGITS_EMPLOYEE_OVERRIDE } from "@digits-shared/session/Session"
import { developerToolsPreference } from "src/shared/config/developerToolsPreferenceKeys"

/**
 * simpleEmployeeFeatures follow a standardized configuration relying on solely the name, description, and employee/user key mapping.
 * Adding a value to this list will enable the feature to be toggled on/off via the internal Employees Features page without additional configuration.
 * Optionally provide a boolean value for `requiresReload` to control the reload behavior.
 * If omitted, the page will not be reloaded when the feature is toggled
 */
const simpleEmployeeFeatures = [
  {
    name: "Grid Overlay",
    description: "Displays an overlay of the design system grid.",
    employeeFeatureKey: "gridOverlayEnabled",
    userPreferenceKey: "gdt",
  },
  {
    name: "Disable Graphql Batching",
    description: "Each operation will be single request",
    employeeFeatureKey: "graphqlNoBatching",
    userPreferenceKey: "graphql-no-batching",
    // this feature is checked in setupApolloClient,
    // so without a reload it would only be updated on legal entity change
    requiresReload: true,
  },
  {
    name: "Close Timeline",
    description: "Enables the desktop close timeline in the period selector slot",
    employeeFeatureKey: "closeTimelineEnabled",
    userPreferenceKey: "close-timeline-enabled",
  },
  {
    name: "Standalone App Details",
    description:
      "Render eligible app details as a standalone view that unmounts the app content, instead of stacking the detail panel over the app content.",
    employeeFeatureKey: "standaloneAppDetailsEnabled",
    userPreferenceKey: "standalone-app-details-enabled",
  },
  {
    name: "User Dashboard",
    description:
      "Enables the user dashboard at /me, a central place to view all your organizations.",
    employeeFeatureKey: "userDashboardEnabled",
    userPreferenceKey: "user-dashboard-enabled",
    // OnboardingTransitionGate reads this once when the app mounts,
    // so without a reload a toggle would only apply on the next load
    requiresReload: true,
  },
  {
    name: "Vault Feature Bounding Boxes",
    description: "Shows document extraction feature bounding boxes in Vault file previews.",
    employeeFeatureKey: "vaultFeatureBoundingBoxesEnabled",
    userPreferenceKey: "vault-feature-bounding-boxes-enabled",
  },
  {
    name: "Budgets",
    description: "Enables the Budgets product and financials expando.",
    employeeFeatureKey: "budgetsEnabled",
    userPreferenceKey: "budgets-enabled",
  },
  {
    name: "Multiple Bill Approvers",
    description: "Enables assigning more than one approver to a bill.",
    employeeFeatureKey: "multipleBillApproversEnabled",
    userPreferenceKey: "multiple-bill-approvers-enabled",
  },
  {
    name: "Pay Modal",
    description: "Enables the payment confirmation modal in Ledger Bills.",
    employeeFeatureKey: "ledgerBillsPayModalEnabled",
    userPreferenceKey: "ledger-bills-pay-modal-enabled",
  },
  {
    name: "Vendor Contacts and Addresses",
    description: "Shows contact and address sections in vendor creation and vendor details.",
    employeeFeatureKey: "vendorContactsAndAddressesEnabled",
    userPreferenceKey: "vendor-contacts-and-addresses-enabled",
  },
] as const satisfies Array<EmployeeFeatureConfig>

/**
 * These features require a non-standardized configuration and must be manually configured within DigitsEmployeeFeatures.tsx
 */
const complexEmployeeFeatures = {
  developerToolsEnabled: developerToolsPreference.key,
  digitsEmployeeOverride: DIGITS_EMPLOYEE_OVERRIDE,
  graphqlTracing: GRAPHQL_TRACE_KEY,
} as const satisfies Record<string, string>

export const SIMPLE_EMPLOYEE_FEATURES = simpleEmployeeFeatures as OptionalRequiresReload<
  typeof simpleEmployeeFeatures
>

/**
 * key-value mapping of named internal employee feature keys to session user preference keys
 * The values of all features in this list can be accessed directly via the hook `useReadEmployeeFeature(employeeFeatureKey)`
 */
export const employeeFeatureKeys = {
  ...complexEmployeeFeatures,
  ...SIMPLE_EMPLOYEE_FEATURES.reduce(
    (acc, f) => ({ ...acc, [f.employeeFeatureKey]: f.userPreferenceKey }),
    {} as {
      [
        K in (typeof SIMPLE_EMPLOYEE_FEATURES)[number] as K["employeeFeatureKey"]
      ]: K["userPreferenceKey"]
    }
  ),
} as const

export type EmployeeFeatureConfig = {
  name: string
  description: string
  employeeFeatureKey: string
  userPreferenceKey: string
  requiresReload?: boolean
}

export type EmployeeFeatureKey = keyof typeof employeeFeatureKeys

export type EmployeeFeatureUserPreferenceKey = (typeof employeeFeatureKeys)[EmployeeFeatureKey]

type OptionalRequiresReload<T extends readonly unknown[]> = {
  [K in keyof T]: T[K] extends { requiresReload: boolean }
    ? T[K]
    : T[K] & { requiresReload: undefined }
}
